
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { blogService } from "@/services/blog.service"
import { courseService } from "@/services/course.service"
import { userService } from "@/services/user.service"
import { useToast } from "@/hooks/use-toast"
import { ArrowLeft, Loader2 } from "lucide-react"
import type { Blog, Theme, User } from "@/types/api"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

const formSchema = z.object({
    title: z.string().min(5, "Название должно содержать не менее 5 символов"),
    slug: z.string().min(2, "Слаг должен содержать не менее 2 символов").max(50, "Слаг не должен превышать 50 символов").regex(/^[a-z0-9-]+$/, "Слаг может содержать только английские буквы (a-z), цифры и дефисы"),
    link: z.string().url("Должна быть действительная ссылка"),
    text: z.string().min(20, "Содержимое блога должно содержать не менее 20 символов"),
    theme_id: z.string().optional(),
    author_id: z.string().optional(),
})

export default function BlogEditPage() {
    const { slug } = useParams()
    const navigate = useNavigate()
    const { toast } = useToast()
    const isEditMode = !!slug

    const [blog, setBlog] = useState<Blog | null>(null)
    const [themes, setThemes] = useState<Theme[]>([])
    const [authors, setAuthors] = useState<User[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            slug: "",
            link: "",
            text: "",
            theme_id: "none",
            author_id: "none",
        },
    })

    useEffect(() => {
        const initData = async () => {
            setIsLoading(true)
            try {
                // Fetch themes and authors
                const [themesRes, authorsRes] = await Promise.all([
                    courseService.getThemes({ limit: 100 }),
                    userService.getUsers({ limit: 100 }),
                ])
                setThemes(themesRes.results)
                setAuthors(authorsRes.results.filter(u => u.is_staff))

                if (isEditMode && slug) {
                    const blogData = await blogService.getBlog(slug)
                    setBlog(blogData)

                    form.reset({
                        title: blogData.title,
                        slug: "",
                        link: blogData.link,
                        text: blogData.text || "",
                        theme_id: blogData.theme?.id ? String(blogData.theme.id) : "none",
                        author_id: blogData.author?.id ? String(blogData.author.id) : "none",
                    })
                }
            } catch (error) {
                console.error(error)
                toast({
                    title: "Ошибка",
                    description: "Не удалось загрузить данные",
                    variant: "destructive",
                })
            } finally {
                setIsLoading(false)
            }
        }

        initData()
    }, [slug, isEditMode])

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsSaving(true)
        try {
            const payload: any = {
                title: values.title,
                link: values.link,
                text: values.text,
                theme_id: values.theme_id && values.theme_id !== "none" ? Number(values.theme_id) : null,
                author_id: values.author_id && values.author_id !== "none" ? Number(values.author_id) : null,
            }

            if (!isEditMode) {
                payload.slug = values.slug
            }

            if (isEditMode && slug) {
                await blogService.updateBlog(slug, payload)
                toast({
                    title: "Успешно",
                    description: "Блог успешно обновлен",
                })
            } else {
                await blogService.createBlog(payload)
                toast({
                    title: "Успешно",
                    description: "Блог успешно создан",
                })
            }
            navigate("/blogs")
        } catch (error: any) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: error?.message || "Не удалось сохранить блог",
                variant: "destructive",
            })
        } finally {
            setIsSaving(false)
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Загрузка...</div>
            </div>
        )
    }

    return (
        <div>
            <div className="mb-6 flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate("/blogs")}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                        {isEditMode ? "Редактировать блог" : "Создать блог"}
                    </h1>
                    <p className="text-gray-600 mt-1">
                        {isEditMode ? "Обновить детали блога" : "Добавить новую запись блога"}
                    </p>
                </div>
            </div>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Детали блога</CardTitle>
                            <CardDescription>
                                Введите основную информацию о записи блога
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <FormField
                                control={form.control}
                                name="title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Название</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Введите название блога" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {!isEditMode && (
                                <FormField
                                    control={form.control}
                                    name="slug"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Слаг</FormLabel>
                                            <FormControl>
                                                <Input placeholder="my-blog-post" {...field} maxLength={50} />
                                            </FormControl>
                                            <FormDescription>
                                                Используйте только английские буквы, цифры и дефисы (макс. 50 символов)
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormField
                                control={form.control}
                                name="link"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Ссылка</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="https://blog.example.com/article"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Внешняя ссылка на полную запись блога
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="text"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Содержимое</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Введите содержимое блога (поддерживает markdown)"
                                                className="min-h-[300px]"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Полное содержимое блога. Поддерживает форматирование markdown.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="theme_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Тема (необязательно)</FormLabel>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Выберите тему" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="none">Нет темы</SelectItem>
                                                    {themes.map((theme) => (
                                                        <SelectItem
                                                            key={theme.id}
                                                            value={String(theme.id)}
                                                        >
                                                            {theme.title}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="author_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Автор (необязательно)</FormLabel>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Выберите автора" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="none">Нет автора</SelectItem>
                                                    {authors.map((author) => (
                                                        <SelectItem
                                                            key={author.id}
                                                            value={String(author.id)}
                                                        >
                                                            {author.full_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex justify-end gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate("/blogs")}
                            disabled={isSaving}
                        >
                            Отмена
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Сохранение...
                                </>
                            ) : (
                                isEditMode ? "Обновить блог" : "Создать блог"
                            )}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    )
}
